import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';
import TechnicianMap from '../../components/fsm/TechnicianMap';

const API = '/api/v1/fsm';

const DispatchBoard = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm') || {};
  const isRTL = lang === 'ar';
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [boardData, setBoardData] = useState({ technicians: [], work_orders: [], unassigned_work_orders: [], unavailability: [] });
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay());
    return d.toISOString().split('T')[0];
  });
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [view, setView] = useState('week');
  const [showLiveMap, setShowLiveMap] = useState(false);

  const getDays = useCallback(() => {
    const days = [];
    const start = new Date(weekStart + 'T00:00:00');
    const count = view === 'week' ? 7 : 1;
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d.toISOString().split('T')[0]);
    }
    return days;
  }, [weekStart, view]);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const days = getDays();
      const from = days[0];
      const to = days[days.length - 1];
      const res = await fetch(`${API}/dispatch/board?from=${from}&to=${to}`, { headers });
      const data = await res.json();
      if (data.success) setBoardData(data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [weekStart, view]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const navigate = (dir) => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() + (dir * (view === 'week' ? 7 : 1)));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const goToday = () => {
    const d = new Date();
    if (view === 'week') d.setDate(d.getDate() - d.getDay());
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const openAssign = async (wo) => {
    setShowAssignModal(wo);
    setLoadingSuggest(true);
    try {
      const res = await fetch(`${API}/dispatch/suggest?work_order_id=${wo.id}`, { headers });
      const data = await res.json();
      if (data.success) setSuggestions(data.data);
    } catch (e) { console.error(e); }
    setLoadingSuggest(false);
  };

  const handleAssign = async (wo, technicianId, schedDate) => {
    try {
      await fetch(`${API}/dispatch/assign`, {
        method: 'POST', headers,
        body: JSON.stringify({ work_order_id: wo.id, technician_id: technicianId, scheduled_date: schedDate || wo.scheduled_date })
      });
      setShowAssignModal(null);
      fetchBoard();
    } catch (e) { console.error(e); }
  };

  const handleUnassign = async (woId) => {
    if (!window.confirm(isRTL ? 'إلغاء التعيين؟' : 'Unassign this work order?')) return;
    try {
      await fetch(`${API}/dispatch/unassign`, { method: 'POST', headers, body: JSON.stringify({ work_order_id: woId }) });
      fetchBoard();
    } catch (e) { console.error(e); }
  };

  const days = getDays();
  const dayNames = isRTL
    ? ['الأحد','الإثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت']
    : ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const today = new Date().toISOString().split('T')[0];

  const priorityColors = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981' };
  const typeIcons = { corrective: '🔧', preventive: '🛡️', inspection: '🔍', emergency: '🚨', installation: '📦' };
  const statusColors = { draft: '#64748b', open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', on_hold: '#94a3b8' };

  const getWOsForCell = (agentId, day) => boardData.work_orders.filter(wo => wo.assigned_to === agentId && wo.scheduled_date?.split('T')[0] === day);
  const isUnavailable = (techId, day) => boardData.unavailability.some(u => {
    const sd = u.start_date?.split('T')[0];
    const ed = u.end_date?.split('T')[0];
    return u.technician_id === techId && day >= sd && day <= ed;
  });
  const getUnavailReason = (techId, day) => {
    const u = boardData.unavailability.find(u => u.technician_id === techId && day >= u.start_date?.split('T')[0] && day <= u.end_date?.split('T')[0]);
    return u ? u.unavailability_type : '';
  };

  const s = {
    card: { background: '#1e293b', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)' },
    btn: (bg) => ({ padding: '8px 16px', borderRadius: 8, border: 'none', background: bg || '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }),
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: '#1e293b', borderRadius: 16, padding: 24, width: '90%', maxWidth: 550, maxHeight: '80vh', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)' },
    woCard: { padding: '6px 8px', borderRadius: 6, fontSize: 11, marginBottom: 4, cursor: 'pointer', transition: 'all 0.15s', lineHeight: 1.3 },
  };

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '20px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ color: '#f1f5f9', fontSize: 24, fontWeight: 700, margin: 0 }}>
            {isRTL ? '📋 لوحة التوزيع' : '📋 Dispatch Board'}
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowLiveMap(true)}
              style={{ ...s.btn('#0f766e'), display: 'flex', alignItems: 'center', gap: 4 }}>
              {isRTL ? '📍 خريطة مباشرة' : '📍 Live Map'}
            </button>
            <button onClick={() => setView(view === 'week' ? 'day' : 'week')}
              style={s.btn('#334155')}>
              {view === 'week' ? (isRTL ? 'يوم' : 'Day') : (isRTL ? 'أسبوع' : 'Week')}
            </button>
            <button onClick={() => navigate(-1)} style={s.btn('#334155')}>←</button>
            <button onClick={goToday} style={s.btn('#334155')}>{isRTL ? 'اليوم' : 'Today'}</button>
            <button onClick={() => navigate(1)} style={s.btn('#334155')}>→</button>
          </div>
        </div>

        {/* Unassigned WOs Bar */}
        {boardData.unassigned_work_orders.length > 0 && (
          <div style={{ ...s.card, padding: 16, marginBottom: 16 }}>
            <div style={{ color: '#f59e0b', fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
              {isRTL ? `⚠️ ${boardData.unassigned_work_orders.length} أمر عمل غير معين` : `⚠️ ${boardData.unassigned_work_orders.length} Unassigned Work Orders`}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {boardData.unassigned_work_orders.map(wo => (
                <div key={wo.id} onClick={() => openAssign(wo)}
                  style={{ ...s.woCard, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px' }}>
                  <span>{typeIcons[wo.wo_type] || '🔧'}</span>
                  <div>
                    <div style={{ color: '#fbbf24', fontWeight: 600 }}>{wo.wo_number}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>{wo.client_name} — {wo.title?.substring(0, 30)}</div>
                    <div style={{ color: '#64748b', fontSize: 10 }}>{wo.scheduled_date?.split('T')[0] || (isRTL ? 'بدون تاريخ' : 'No date')}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColors[wo.priority] || '#64748b', marginLeft: 'auto' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
        ) : boardData.technicians.length === 0 ? (
          <div style={{ ...s.card, padding: 60, textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👷</div>
            <div style={{ fontSize: 16 }}>{isRTL ? 'لا يوجد فنيين. أضف فنيين من صفحة إدارة الفنيين أولاً.' : 'No technicians found. Add technicians from the Technicians page first.'}</div>
          </div>
        ) : (
          <div style={{ ...s.card, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: view === 'week' ? 900 : 400 }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 14px', textAlign: isRTL ? 'right' : 'left', color: '#64748b', fontWeight: 600, fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0f172a', position: 'sticky', left: 0, zIndex: 2, minWidth: 140 }}>
                    {isRTL ? 'الفني' : 'Technician'}
                  </th>
                  {days.map(day => {
                    const d = new Date(day + 'T00:00:00');
                    const isToday = day === today;
                    return (
                      <th key={day} style={{ padding: '10px 8px', textAlign: 'center', color: isToday ? '#3b82f6' : '#64748b', fontWeight: 600, fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.08)', background: isToday ? 'rgba(59,130,246,0.08)' : '#0f172a', minWidth: view === 'week' ? 110 : 300 }}>
                        <div>{dayNames[d.getDay()]}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: isToday ? '#60a5fa' : '#94a3b8' }}>{d.getDate()}</div>
                        <div style={{ fontSize: 10, color: '#475569' }}>{d.toLocaleDateString('en', { month: 'short' })}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {boardData.technicians.map(tech => (
                  <tr key={tech.technician_id}>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: '#0f172a', position: 'sticky', left: 0, zIndex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: tech.color || '#666', flexShrink: 0 }} />
                        <div>
                          <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 13 }}>{tech.full_name}</div>
                          <div style={{ color: '#64748b', fontSize: 11 }}>{isRTL ? `أقصى: ${tech.max_daily_work_orders}` : `Max: ${tech.max_daily_work_orders}/day`}</div>
                        </div>
                      </div>
                    </td>
                    {days.map(day => {
                      const wos = getWOsForCell(tech.agent_id, day);
                      const unavail = isUnavailable(tech.technician_id, day);
                      const isToday = day === today;
                      return (
                        <td key={day} style={{
                          padding: 4, borderBottom: '1px solid rgba(255,255,255,0.05)',
                          background: unavail ? 'rgba(239,68,68,0.06)' : isToday ? 'rgba(59,130,246,0.04)' : 'transparent',
                          verticalAlign: 'top', minHeight: 60
                        }}>
                          {unavail && (
                            <div style={{ padding: '4px 8px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 10, textAlign: 'center', marginBottom: 4 }}>
                              {getUnavailReason(tech.technician_id, day) === 'sick' ? '🤒' : '🚫'} {getUnavailReason(tech.technician_id, day)}
                            </div>
                          )}
                          {wos.map(wo => (
                            <div key={wo.id}
                              onClick={() => openAssign(wo)}
                              style={{ ...s.woCard,
                                background: `${tech.color || '#3b82f6'}22`,
                                borderLeft: isRTL ? 'none' : `3px solid ${priorityColors[wo.priority] || '#64748b'}`,
                                borderRight: isRTL ? `3px solid ${priorityColors[wo.priority] || '#64748b'}` : 'none',
                              }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{wo.wo_number}</span>
                                <span style={{ fontSize: 10 }}>{typeIcons[wo.wo_type] || ''}</span>
                              </div>
                              <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 2 }}>{wo.client_name}</div>
                              {wo.scheduled_time_start && (
                                <div style={{ color: '#64748b', fontSize: 10 }}>🕐 {wo.scheduled_time_start?.substring(0,5)}{wo.scheduled_time_end ? `-${wo.scheduled_time_end.substring(0,5)}` : ''}</div>
                              )}
                              <div style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, background: statusColors[wo.status] || '#334155', color: '#fff', marginTop: 3 }}>
                                {wo.status}
                              </div>
                            </div>
                          ))}
                          {!unavail && wos.length < tech.max_daily_work_orders && wos.length === 0 && (
                            <div style={{ padding: 8, textAlign: 'center', color: '#334155', fontSize: 18 }}>·</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <div style={s.overlay} onClick={() => setShowAssignModal(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <h2 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: 18 }}>
                {isRTL ? 'تعيين أمر العمل' : 'Assign Work Order'}
              </h2>

              <div style={{ padding: 14, background: '#0f172a', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#60a5fa', fontWeight: 700 }}>{showAssignModal.wo_number}</span>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: priorityColors[showAssignModal.priority], color: '#fff' }}>
                    {showAssignModal.priority}
                  </span>
                </div>
                <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 6 }}>{showAssignModal.title}</div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
                  {showAssignModal.client_name && `🏭 ${showAssignModal.client_name}`}
                  {showAssignModal.equipment_name && ` · 🔧 ${showAssignModal.equipment_name}`}
                  {showAssignModal.category_name && ` (${showAssignModal.category_name})`}
                </div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                  📅 {showAssignModal.scheduled_date?.split('T')[0] || (isRTL ? 'بدون تاريخ' : 'No date')}
                  {showAssignModal.location_name && ` · 📍 ${showAssignModal.location_name}`}
                </div>
                {showAssignModal.assigned_to && (
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => handleUnassign(showAssignModal.id)} style={{ ...s.btn('#7f1d1d'), padding: '5px 12px', fontSize: 12 }}>
                      {isRTL ? 'إلغاء التعيين' : 'Unassign'}
                    </button>
                  </div>
                )}
              </div>

              <h3 style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                {isRTL ? 'الفنيين المقترحين' : 'Suggested Technicians'}
              </h3>

              {loadingSuggest ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>Loading...</div>
              ) : suggestions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>
                  {isRTL ? 'لا يوجد فنيين متاحين لهذا النوع من المعدات' : 'No available technicians with matching skills'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {suggestions.map(sg => {
                    const atCapacity = sg.day_wo_count >= sg.max_daily_work_orders;
                    return (
                      <div key={sg.technician_id}
                        onClick={() => !atCapacity && handleAssign(showAssignModal, sg.technician_id)}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px 14px', background: atCapacity ? 'rgba(100,116,139,0.1)' : 'rgba(59,130,246,0.08)',
                          borderRadius: 10, cursor: atCapacity ? 'not-allowed' : 'pointer',
                          border: '1px solid', borderColor: atCapacity ? 'rgba(100,116,139,0.15)' : 'rgba(59,130,246,0.2)',
                          opacity: atCapacity ? 0.5 : 1, transition: 'all 0.15s'
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: sg.color || '#666' }} />
                          <div>
                            <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>{sg.full_name}</div>
                            <div style={{ color: '#64748b', fontSize: 12 }}>
                              {isRTL ? 'مستوى:' : 'Level:'} {sg.skill_level || 'N/A'} · {isRTL ? 'اليوم:' : 'Today:'} {sg.day_wo_count}/{sg.max_daily_work_orders}
                              {atCapacity && <span style={{ color: '#ef4444', marginLeft: 6 }}>{isRTL ? '(ممتلئ)' : '(Full)'}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ padding: '4px 10px', borderRadius: 6, background: sg.score >= 110 ? '#065f46' : sg.score >= 100 ? '#1e3a5f' : '#7f1d1d', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                            {sg.score}
                          </div>
                          {!atCapacity && <span style={{ color: '#3b82f6', fontSize: 18 }}>→</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setShowAssignModal(null)} style={s.btn('#334155')}>{isRTL ? 'إغلاق' : 'Close'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <TechnicianMap isVisible={showLiveMap} onClose={() => setShowLiveMap(false)} />
    </Layout>
  );
};

export default DispatchBoard;
